import { Injectable, inject } from '@angular/core'
import { PermissionService } from '@core/services/permission-service/permission-service'
import { PERMISSIONS } from '@core/constants/permissions.constants'
import {
    SHIKSHA_FEATURES,
    SHIKSHA_PERMISSION_CONFIG,
    ShikshaFeature,
} from '@core/constants/shiksha-permissions.constants'

@Injectable({
    providedIn: 'root',
})
export class ShikshaPermissionsService {
    private permissionService = inject(PermissionService)
    private permissionCache = new Map<string, boolean>()
    private featureCache = new Map<string, boolean>()

    hasAnyShikshaPermission(): boolean {
        return (
            this.permissionService.getPermissionByName(PERMISSIONS.CREATE_CLIENT_SHIKSHA) === true
        )
    }

    isShikshaFeature(feature: ShikshaFeature): boolean {
        if (this.featureCache.has(feature)) {
            return this.featureCache.get(feature) as boolean
        }

        const requiredPermissions = SHIKSHA_PERMISSION_CONFIG[feature]

        if (!requiredPermissions) {
            this.featureCache.set(feature, false)
            return false
        }

        const hasPermission = requiredPermissions.some((permission) => {
            if (this.permissionCache.has(permission)) {
                return this.permissionCache.get(permission) as boolean
            }

            const permissionValue = this.permissionService.getPermissionByName(permission)
            const isEnabled = permissionValue === true
            this.permissionCache.set(permission, isEnabled)
            return isEnabled
        })

        this.featureCache.set(feature, hasPermission)
        return hasPermission
    }

    hasAllShikshaFeatures(features: ShikshaFeature[]): boolean {
        return features.every((feature) => this.isShikshaFeature(feature))
    }

    hasAnyShikshaFeature(features: ShikshaFeature[]): boolean {
        return features.some((feature) => this.isShikshaFeature(feature))
    }

    getEnabledShikshaFeatures(): ShikshaFeature[] {
        const allFeatures = Object.keys(SHIKSHA_PERMISSION_CONFIG) as ShikshaFeature[]
        return allFeatures.filter((feature) => this.isShikshaFeature(feature))
    }

    canViewDeal(): boolean {
        const cacheKey = 'canViewDeal'
        if (this.permissionCache.has(cacheKey)) {
            return this.permissionCache.get(cacheKey) as boolean
        }
        const hasPermission = this.isShikshaFeature(SHIKSHA_FEATURES.VIEW_DEAL)
        this.permissionCache.set(cacheKey, hasPermission)
        return hasPermission
    }

    canCreateDeal(): boolean {
        const cacheKey = 'canCreateDeal'
        if (this.permissionCache.has(cacheKey)) {
            return this.permissionCache.get(cacheKey) as boolean
        }
        const hasPermission = this.isShikshaFeature(SHIKSHA_FEATURES.CREATE_DEAL)
        this.permissionCache.set(cacheKey, hasPermission)
        return hasPermission
    }

    canViewDealDetails(): boolean {
        return this.canViewDeal()
    }

    canCreateOrEditContact(): boolean {
        const cacheKey = 'canCreateOrEditContact'
        if (this.permissionCache.has(cacheKey)) {
            return this.permissionCache.get(cacheKey) as boolean
        }
        const canCreateDealPermission = this.canCreateDeal()
        const canCreateClientPermission = this.isShikshaFeature(SHIKSHA_FEATURES.CREATE)
        const hasPermission = canCreateDealPermission || canCreateClientPermission
        this.permissionCache.set(cacheKey, hasPermission)
        return hasPermission
    }

    canReassignDeal(): boolean {
        return this.canCreateDeal()
    }
}
